




















































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import HasValidation from "@/mixins/HasValidation";
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { BrandConfigKeys } from "@/data/constants";
import {
  InvoiceConsolidationRuleTypes as ICRT,
  InvoiceConsolidationTypes as ICT
} from "@/data/enums/invoice";
import { DataModules } from "@/store/modules/data";

// Types
import { IState } from "@/store";
import { IClient } from "@/models/clients";
import { IBrand } from "@/models/brands";

@Component({
  computed: {
    ...mapState({
      baseRule(state: IState) {
        return state.brand.config[
          BrandConfigKeys.INVOICE_CONSOLIDATION_BASE_RULE
        ];
      },
      baseRuleDayOfWeek(state: IState) {
        return state.brand.config[
          BrandConfigKeys.INVOICE_CONSOLIDATION_WEEK_DAY
        ];
      },
      baseRuleDayOfMonth(state: IState) {
        return state.brand.config[BrandConfigKeys.INVOICE_CONSOLIDATION_DATE];
      },
      invoiceConsolidation(state: IState) {
        return state.brand.config[
          BrandConfigKeys.INVOICE_CONSOLIDATION_ENABLED
        ];
      },
      brandId(state: IState) {
        return state.brand.id;
      }
    })
  },
  components: {
    "client-invoice-consolidation-rule-field": () =>
      import(
        "@/components/app/global/client/clientInvoiceConsolidationRuleField.vue"
      ),
    "base-rule-day-of-week-field": () =>
      import("@/components/app/global/client/baseRuleDayOfWeekField.vue"),
    "base-rule-day-of-month-field": () =>
      import("@/components/app/global/client/baseRuleDayOfMonthField.vue")
  }
})
export default class ClientInvoiceConsolidationForm extends Mixins(
  HasValidation,
  RequestPromises
) {
  // Map State

  public baseRule!: IClient["invoice_consolidation_base_rule"];
  public baseRuleDayOfMonth!: IClient["invoice_consolidation_base_rule_date_of_month_day"];
  public baseRuleDayOfWeek!: IClient["invoice_consolidation_base_rule_day_of_week"];
  public invoiceConsolidation!: IClient["invoice_consolidation_enabled"];
  public brandId!: IBrand["id"];

  // Props

  @Prop({ required: true })
  public client!: IClient;

  @Prop({ default: null })
  public label!: string | null;

  // Data

  public isLoading = false;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  public form = {} as Pick<
    IClient,
    | "invoice_consolidation_enabled"
    | "invoice_consolidation_base_rule_day_of_week"
    | "invoice_consolidation_base_rule_date_of_month_day"
    | "invoice_consolidation_base_rule"
  >;

  // Life-cycle methods

  public created() {
    this.isLoading = true;
    this.loadData();
  }

  // Methods

  public async loadData() {
    try {
      await this.$store.dispatch("brand/getConfig", {
        keys: [
          BrandConfigKeys.INVOICE_CONSOLIDATION_BASE_RULE,
          BrandConfigKeys.INVOICE_CONSOLIDATION_DATE,
          BrandConfigKeys.INVOICE_CONSOLIDATION_ENABLED,
          BrandConfigKeys.INVOICE_CONSOLIDATION_WEEK_DAY
        ]
      });
      this.initialiseForm();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public initialiseForm() {
    this.form = this.initForm();
  }

  public initForm() {
    return _.pick(this.client, [
      "invoice_consolidation_enabled",
      "invoice_consolidation_base_rule_day_of_week",
      "invoice_consolidation_base_rule_date_of_month_day",
      "invoice_consolidation_base_rule"
    ]);
  }

  public async validateBeforeSubmit() {
    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      this.isProcessing = true;

      await this.updateClient();
      this.$emit("refreshClient");

      this.$store.dispatch("toast/show", {
        message: this.$t("_sentence.invoice.consolidation_preferences_updated")
      });
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public updateClient() {
    return this.$store.dispatch(`data/${DataModules.CLIENTS}/update`, {
      id: this.clientId,
      data: this.form,
      vm: this
    });
  }

  public revertToInitialState() {
    this.initialiseForm();
  }

  // Computed

  public get clientId() {
    return this.client.id;
  }

  public get invoiceConsolidationOptions() {
    return [
      {
        value: ICT.ENABLED,
        leftLabel: this.$t("_sentence.invoice.enable_consolidation_label")
      },
      {
        value: ICT.DISABLED,
        leftLabel: this.$t("_sentence.invoice.disable_consolidation_label")
      }
    ];
  }

  public get invoiceConsolidationInheritValue() {
    return ICT.INHERIT;
  }

  public get showBasicRuleFields() {
    return (
      this.form.invoice_consolidation_enabled === ICT.ENABLED ||
      (this.form.invoice_consolidation_enabled === ICT.INHERIT &&
        this.enabledBV === ICT.ENABLED)
    );
  }

  public get showDayOfWeekField() {
    return (
      this.form.invoice_consolidation_base_rule === ICRT.DAY_OF_WEEK ||
      (_.isNull(this.form.invoice_consolidation_base_rule) &&
        this.baseRule === ICRT.DAY_OF_WEEK)
    );
  }

  public get showDayOfMonthField() {
    return (
      this.form.invoice_consolidation_base_rule === ICRT.DAY_OF_MONTH ||
      (_.isNull(this.form.invoice_consolidation_base_rule) &&
        this.baseRule === ICRT.DAY_OF_MONTH)
    );
  }

  public get brandInvoiceConsolidationLabel() {
    return (this.invoiceConsolidation
      ? this.$t("_sentence.invoice.enable_consolidation_label")
      : this.$t("_sentence.invoice.disable_consolidation_label")
    ).toString();
  }

  public get enabledBV() {
    return this.$store.getters["brand/config"][
      BrandConfigKeys.INVOICE_CONSOLIDATION_ENABLED
    ]
      ? ICT.ENABLED
      : ICT.DISABLED;
  }

  public get formIsDisabled() {
    return this.isProcessing || !this.formIsChanged || this.hasErrors;
  }

  public get formIsChanged() {
    return !this.$_.isEqual(this.form, this.initForm());
  }

  // Watchers

  @Watch("client")
  public onClientDataChange() {
    this.initialiseForm();
  }

  @Watch("formIsChanged")
  public onIsEnabledChange() {
    this.$emit("isEnabled", this.formIsChanged);
  }

  @Watch("isProcessing")
  public onIsProcessingChange() {
    this.$emit("isProcessing", this.isProcessing);
  }
}
